import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { HttpError } from '../api';

const TOAST_MS = 6000;

// Surface react-query mutation failures globally so a failed write (e.g.
// creating an event against an account whose token has been revoked) doesn't
// just silently vanish from the optimistic cache. Sits above the sync dot.
export default function MutationErrorToast() {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const [seq, setSeq] = useState(0);

  useEffect(() => {
    const cache = qc.getMutationCache();
    const seen = new WeakSet<object>();
    const unsub = cache.subscribe((event) => {
      if (event.type !== 'updated') return;
      const m = event.mutation;
      if (m.state.status !== 'error') return;
      if (seen.has(m)) return;
      seen.add(m);

      // The sync indicator already renders sync errors; don't double-show.
      const key = m.options.mutationKey;
      if (Array.isArray(key) && key[0] === 'sync') return;

      const err = m.state.error as unknown;
      let text = 'Action failed';
      if (err instanceof HttpError) text = err.message || text;
      else if (err instanceof Error && err.message) text = err.message;
      setMsg(text);
      setSeq((n) => n + 1);
    });
    return unsub;
  }, [qc]);

  useEffect(() => {
    if (!msg) return;
    const t = window.setTimeout(() => setMsg(null), TOAST_MS);
    return () => window.clearTimeout(t);
  }, [msg, seq]);

  if (!msg) return null;
  return (
    <div
      role="alert"
      className="fixed bottom-12 right-3 z-50 max-w-[320px] rounded-md border border-[var(--today-fg)] bg-[var(--popover-bg)] px-3 py-2 text-[0.85rem] shadow-[var(--shadow-md)]"
      style={{ color: 'var(--today-fg)' }}
    >
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setMsg(null)}
        className="float-right -mr-1 -mt-[2px] ml-2 text-[var(--text-faint)] hover:text-[var(--text)]"
      >
        ×
      </button>
      {msg}
    </div>
  );
}
